/* WIP: App theme file for StyledComponent's <ThemeProvider/> */
export type colors =
  | SurfaceColors
  | HeaderColors
  | "text"
  | "primary"
  | "secondary"
  | "accent"
  | "create"
  | "caution"
  | "link"
  // 'generic' focus. Individual intents may override via glowColor
  | "focus"
  | "danger";

type hsl = [number, number, number];
export type colorTypes = {
  color: hsl;
  textColor?: hsl;
  contrastTextColor?: hsl;
  glowColor?: hsl;
};

type SurfaceColors =
  | "surface0"
  | "surface1"
  | "surface2"
  | "surface3"
  | "surface4"
  | "surface5"
  | "surface6";

type HeaderColors = "h1" | "h2" | "h3" | "h4";

const text: colorTypes = { color: [0, 0, 30] };

const surfaces: Record<SurfaceColors, colorTypes> = {
  surface0: { color: [0, 0, 100], contrastTextColor: text.color },
  surface1: { color: [0, 0, 98], contrastTextColor: text.color },
  surface2: { color: [0, 0, 96], contrastTextColor: text.color },
  surface3: { color: [0, 0, 94], contrastTextColor: text.color },
  surface4: { color: [0, 0, 92], contrastTextColor: text.color },
  surface5: { color: [0, 0, 90], contrastTextColor: text.color },
  surface6: { color: [0, 0, 80], contrastTextColor: text.color },
};

const caution: colorTypes = {
  color: [33, 100, 50],
  contrastTextColor: [0, 0, 100],
}; // hsl(33, 100%, 50%)

const danger: colorTypes = {
  color: [350, 56, 50],
  contrastTextColor: [0, 0, 100],
}; // hsl(350, 56%, 50%)

const create: colorTypes = {
  color: [123, 20, 50],
  contrastTextColor: [0, 0, 100],
}; // hsl(123, 20%, 50%)

const commonHeaders: Record<HeaderColors, colorTypes> = {
  h1: { color: [200, 100, 30] },
  h2: { color: [203, 80, 50] },
  h3: { color: [0, 0, 47] },
  h4: { color: [0, 0, 47] },
};

export interface CommonDefaultTheme {
  colors: Record<colors, colorTypes> | undefined;
}
const themes: {
  light: CommonDefaultTheme;
  bonfire: CommonDefaultTheme;
  gw: CommonDefaultTheme;
} = {
  light: {
    colors: {
      ...surfaces,
      ...commonHeaders,
      secondary: {
        color: [200, 0, 60], // hsl(0, 0%, 60%)
        textColor: [0, 0, 30], // hsl(0, 0%, 30%)
        contrastTextColor: [0, 0, 20], // hsl(0, 0%, 20%)
        glowColor: [203, 80, 50], // hsl(203, 80%, 65%)
      },
      primary: {
        color: [203, 80, 50], // hsl(203, 80%, 50%),
        contrastTextColor: [0, 0, 100], // hsl(0, 0, 100%),
      },
      link: {
        color: [203, 80, 50], // hsl(203, 80%, 50%),
        contrastTextColor: [0, 0, 100], // hsl(0, 0, 100%),
      },
      focus: {
        color: [203, 80, 50], // hsl(203, 80%, 50%),
        contrastTextColor: [0, 0, 100], // hsl(0, 0, 100%),
      },
      accent: {
        color: [200, 100, 30], // hsl(203, 80%, 50%),
        contrastTextColor: [0, 0, 100], // hsl(0, 0, 100%),
      },
      caution,
      danger,
      create,
      text,
    },
  },
  // these are not final
  bonfire: {
    colors: {
      ...surfaces,
      ...commonHeaders,
      secondary: {
        color: [0, 0, 60], // hsl(0, 0%, 60%)
        textColor: [0, 0, 30], // hsl(0, 0%, 30%)
        contrastTextColor: [0, 0, 20], // hsl(0, 0%, 20%)
        glowColor: [203, 80, 50], // hsl(203, 80%, 65%)
      },
      primary: {
        color: [250, 48, 53], // hsl(250, 48%, 53%)
        contrastTextColor: [0, 0, 100], // hsl(0, 0%, 100%)
      },
      link: {
        color: [250, 48, 53], // hsl(250, 48%, 53%)
        contrastTextColor: [0, 0, 100], // hsl(0, 0%, 100%)
      },
      accent: {
        color: [200, 100, 30],
        contrastTextColor: [0, 0, 100],
      },
      focus: {
        color: [250, 48, 53], // hsl(250, 48%, 53%)
        contrastTextColor: [0, 0, 100], // hsl(0, 0%, 100%)
      },
      caution,
      danger,
      create,
      text,
    },
  },
  gw: {
    colors: {
      ...surfaces,
      ...commonHeaders,
      h1: {
        color: [240, 2, 35],
      },
      h2: {
        color: [240, 2, 35],
      },
      h3: {
        color: [240, 2, 35],
      },
      secondary: {
        color: [0, 0, 60], // hsl(0, 0%, 60%)
        textColor: [0, 0, 30], // hsl(0, 0%, 30%)
        contrastTextColor: [0, 0, 20], // hsl(0, 0%, 20%)
        glowColor: [203, 80, 50], // hsl(203, 80%, 65%)
      },
      primary: {
        color: [104, 96, 27], // hsl(104, 96%, 27%)
        contrastTextColor: [0, 0, 100], // hsl(0, 0%, 100%)
      },
      link: {
        color: [204, 100, 31], // hsl(104, 96%, 27%)
        contrastTextColor: [0, 0, 100], // hsl(0, 0%, 100%)
      },
      focus: {
        color: [204, 100, 31], // hsl(104, 96%, 27%)
        contrastTextColor: [0, 0, 100], // hsl(0, 0%, 100%)
      },
      accent: {
        color: [204, 100, 31],
        contrastTextColor: [0, 0, 100],
      },
      caution,
      danger,
      // GW's create color is so close to their primary color that we just set it to the same thing
      create: {
        color: [104, 96, 27], // hsl(104, 96%, 27%)
        contrastTextColor: [0, 0, 100], // hsl(0, 0%, 100%)
      },
      text: {
        color: [0, 0, 0],
        contrastTextColor: [0, 0, 100],
      },
    },
  },
};

export default themes;
